import { Dayjs } from 'dayjs'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { OLMSApiService } from '@/shared/apis/'
import { EntityId } from '@reduxjs/toolkit'

import { HistoricalTraceData, RawTracePoint, TracePoint } from '../models/HistoricalTrace'

export type ReplayState = {
  isReplayPanelVisible: boolean
  activeTraceIndex: number | null
  traces: HistoricalTraceData[]
  isReplaying: boolean
  isLoading: boolean
  playbackSpeed: number
  currentPointIndex: number
  showProcessedPoints: boolean
  showRawPoints: boolean
  showRoute: boolean
}

type ReplayContextType = ReplayState & {
  loadReplayData: (runId: EntityId, runName: string, uiColor: string, from: Dayjs, to: Dayjs) => Promise<void>
  clearReplayData: (traceIndex?: number) => void
  toggleReplay: () => void
  setPlaybackSpeed: (speed: number) => void
  jumpToPoint: (index: number) => void
  setActiveTraceIndex: (index: number | null) => void
  toggleProcessedPoints: () => void
  toggleRawPoints: () => void
  toggleRoute: () => void
  setIsReplayPanelVisible: (visible: boolean) => void
}

const ReplayContext = createContext<ReplayContextType | undefined>(undefined)

export const useReplayContext = () => {
  const context = useContext(ReplayContext)

  if (!context) {
    throw new Error('useReplayContext must be used within a ReplayContextProvider')
  }

  return context
}

export const ReplayContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [replayState, setReplayState] = useState<ReplayState>({
    isReplayPanelVisible: false,
    activeTraceIndex: null,
    traces: [],
    isLoading: false,
    isReplaying: false,
    playbackSpeed: 1,
    currentPointIndex: 0,
    showProcessedPoints: true,
    showRawPoints: false,
    showRoute: true,
  })

  const playbackIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const requestRef = useRef<AbortController | null>(null)

  // Handle playback animation using requestAnimationFrame for smoother playback
  useEffect(() => {
    if (!replayState.isReplaying || replayState.activeTraceIndex === null) {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current)
        playbackIntervalRef.current = null
      }

      return
    }

    const activeTrace = replayState.traces[replayState.activeTraceIndex]

    if (!activeTrace) return

    // Use a more responsive interval based on playback speed
    // Lower interval time means more frequent updates for smoother animation
    const intervalTime = Math.max(30, 500 / replayState.playbackSpeed)

    playbackIntervalRef.current = setInterval(() => {
      setReplayState(prev => {
        const nextPointIndex = prev.currentPointIndex + 1

        // If reached end of points, stop playback
        if (nextPointIndex >= activeTrace.tracePoints.length) {
          if (playbackIntervalRef.current) {
            clearInterval(playbackIntervalRef.current)
            playbackIntervalRef.current = null
          }

          return {
            ...prev,
            isReplaying: false,
            currentPointIndex: 0, // Reset to beginning
          }
        }

        return {
          ...prev,
          currentPointIndex: nextPointIndex,
        }
      })
    }, intervalTime)

    return () => {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current)
        playbackIntervalRef.current = null
      }
    }
  }, [replayState.isReplaying, replayState.activeTraceIndex, replayState.playbackSpeed, replayState.traces])

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current)
      }

      if (requestRef.current) {
        requestRef.current.abort()
      }
    }
  }, [])

  /**
   * Optimizes trace data by removing duplicate or unnecessary points
   * while preserving the chronological sequence of events
   */
  const optimizeTraceData = useCallback((data: HistoricalTraceData): HistoricalTraceData => {
    // Process trace points and remove duplicates or very close points
    const processedPoints: TracePoint[] = []
    const rawPoints: RawTracePoint[] = []

    // Time-based thinning - keep at most 1 point per second for smoother playback
    const timeMap = new Map<string, TracePoint>()

    data.tracePoints.forEach(point => {
      if (!point.x || !point.y) return

      // Generate a time key rounded to the nearest second
      const timestamp = new Date(point.deviceTime).getTime()
      const timeKey = Math.floor(timestamp / 1000).toString()

      // Keep only one point per second, prioritizing higher accuracy points
      if (!timeMap.has(timeKey) || point.accuracy < timeMap.get(timeKey)!.accuracy) {
        timeMap.set(timeKey, point)
      }
    })

    // Convert the map back to an array and sort by time
    processedPoints.push(...Array.from(timeMap.values()))
    processedPoints.sort((a, b) =>
      new Date(a.deviceTime).getTime() - new Date(b.deviceTime).getTime(),
    )

    // For raw points, use a spatial grid to thin the data while maintaining distribution
    const spatialGrid = new Map<string, RawTracePoint>()
    const gridSize = 0.0005 // About 50 meters at equator

    data.actualTracePoints.forEach(point => {
      if (!point.x || !point.y) return

      // Create a grid cell key
      const gridX = Math.floor(point.x / gridSize)
      const gridY = Math.floor(point.y / gridSize)
      const gridKey = `${gridX},${gridY}`

      // Keep only one point per grid cell, prioritizing newer points
      if (!spatialGrid.has(gridKey) ||
        new Date(point.deviceTime) > new Date(spatialGrid.get(gridKey)!.deviceTime)) {
        spatialGrid.set(gridKey, point)
      }
    })

    // Convert grid map to array and sort by time
    rawPoints.push(...Array.from(spatialGrid.values()))
    rawPoints.sort((a, b) =>
      new Date(a.deviceTime).getTime() - new Date(b.deviceTime).getTime(),
    )

    // Simplify waypoints by removing points too close together
    const simplifiedWaypoints = []
    let lastPoint = null

    for (const point of data.wayPoints) {
      if (!point.x || !point.y) continue

      if (!lastPoint ||
        Math.abs(point.x - lastPoint.x) > 0.0001 ||
        Math.abs(point.y - lastPoint.y) > 0.0001) {
        simplifiedWaypoints.push(point)
        lastPoint = point
      }
    }

    // Return optimized data
    return {
      ...data,
      tracePoints: processedPoints,
      actualTracePoints: rawPoints,
      wayPoints: simplifiedWaypoints,
    }
  }, [])

  // Function to load replay data
  const loadReplayData = async (runId: EntityId, runName: string, uiColor: string, from: Dayjs, to: Dayjs) => {
    setReplayState(prev => ({
      ...prev,
      isLoading: true,
    }))

    try {
      // Cancel any in-progress requests
      if (requestRef.current) {
        requestRef.current.abort()
      }

      // Set up new abort controller for this request
      requestRef.current = new AbortController()

      const signal = requestRef.current.signal

      const fromStr = from.format('YYYY-MM-DD')
      const toStr = to.format('YYYY-MM-DD')

      const response = await OLMSApiService.post<HistoricalTraceData>('/tracking/replay', {
        id: runId,
        from: fromStr,
        to: toStr,
        includeRawPoints: true,
        sourceType: 1, // Mobile GPS
      }, { signal })

      if (!response) {
        throw new Error('Failed to load replay data')
      }

      // Apply color to all points for easier rendering
      response.tracePoints.forEach(point => {
        point.uiColor = uiColor
      })

      response.actualTracePoints.forEach(point => {
        point.uiColor = uiColor
      })

      // Create and optimize trace data
      const newTrace = optimizeTraceData({
        runId,
        runName,
        uiColor,
        dateFrom: fromStr,
        dateTo: toStr,
        tracePoints: response.tracePoints || [],
        actualTracePoints: response.actualTracePoints || [],
        wayPoints: response.wayPoints || [],
      })

      setReplayState(prev => {
        // Check if trace for this run already exists
        const existingIndex = prev.traces.findIndex(t => t.runId === runId)
        const newTraces = [...prev.traces]

        if (existingIndex >= 0) {
          // Replace existing trace
          newTraces[existingIndex] = newTrace
        } else {
          // Add new trace
          newTraces.push(newTrace)
        }

        return {
          ...prev,
          traces: newTraces,
          activeTraceIndex: existingIndex >= 0 ? existingIndex : newTraces.length - 1,
          currentPointIndex: 0,
          isReplaying: false,
        }
      })
    } catch (error) {
      // Ignore abort errors as they're expected
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Request was aborted')

        return
      }

      console.error('Error loading replay data:', error)
      throw error
    } finally {
      requestRef.current = null
    }

    setReplayState(prev => ({
      ...prev,
      isLoading: false,
    }))
  }

  // Function implementations
  const clearReplayData = (traceIndex?: number) => {
    if (playbackIntervalRef.current) {
      clearInterval(playbackIntervalRef.current)
      playbackIntervalRef.current = null
    }

    if (traceIndex !== undefined) {
      // Remove specific trace
      setReplayState(prev => {
        const newTraces = [...prev.traces]

        newTraces.splice(traceIndex, 1)

        return {
          ...prev,
          traces: newTraces,
          activeTraceIndex: newTraces.length > 0 ? 0 : null,
          isReplaying: false,
          currentPointIndex: 0,
        }
      })
    } else {
      // Clear all traces
      setReplayState(prev => ({
        ...prev,
        traces: [],
        activeTraceIndex: null,
        isReplaying: false,
        currentPointIndex: 0,
      }))
    }
  }

  const toggleReplay = useCallback(() => {
    setReplayState(prev => ({
      ...prev,
      isReplaying: !prev.isReplaying,
    }))
  }, [])

  const setPlaybackSpeed = useCallback((speed: number) => {
    setReplayState(prev => ({
      ...prev,
      playbackSpeed: speed,
    }))
  }, [])

  const jumpToPoint = useCallback((index: number) => {
    setReplayState(prev => {
      if (prev.activeTraceIndex === null) return prev

      const activeTrace = prev.traces[prev.activeTraceIndex]

      if (!activeTrace) return prev

      let validIndex = index

      if (validIndex < 0) validIndex = 0
      if (validIndex >= activeTrace.tracePoints.length) validIndex = activeTrace.tracePoints.length - 1

      return {
        ...prev,
        currentPointIndex: validIndex,
      }
    })
  }, [])

  const setActiveTraceIndex = useCallback((index: number | null) => {
    setReplayState(prev => ({
      ...prev,
      activeTraceIndex: index,
      currentPointIndex: 0,
      isReplaying: false,
    }))
  }, [])

  const toggleProcessedPoints = useCallback(() => {
    setReplayState(prev => ({
      ...prev,
      showProcessedPoints: !prev.showProcessedPoints,
    }))
  }, [])

  const toggleRawPoints = useCallback(() => {
    setReplayState(prev => ({
      ...prev,
      showRawPoints: !prev.showRawPoints,
    }))
  }, [])

  const toggleRoute = useCallback(() => {
    setReplayState(prev => ({
      ...prev,
      showRoute: !prev.showRoute,
    }))
  }, [])

  const setIsReplayPanelVisible = useCallback((visible: boolean) => {
    setReplayState(prev => ({
      ...prev,
      isReplayPanelVisible: visible,
    }))
  }, [])

  const value = useMemo(() => ({
    ...replayState,
    loadReplayData,
    clearReplayData,
    toggleReplay,
    setPlaybackSpeed,
    jumpToPoint,
    setActiveTraceIndex,
    toggleProcessedPoints,
    toggleRawPoints,
    toggleRoute,
    setIsReplayPanelVisible,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [
    replayState,
    toggleReplay,
    setPlaybackSpeed,
    jumpToPoint,
    setActiveTraceIndex,
    toggleProcessedPoints,
    toggleRawPoints,
    toggleRoute,
    setIsReplayPanelVisible,
  ])

  return (
    <ReplayContext.Provider value={value}>
      {children}
    </ReplayContext.Provider>
  )
}